import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { usePlatform } from "@/context/PlatformContext";
import { JasrMark } from "@/components/JasrLogo";
import { api } from "@/lib/api";
import { Clock, ChevronLeft, BookOpen, ArrowLeft } from "lucide-react";

const CATEGORY_COLORS = {
  "نمو المبيعات":   { bg: "#10B981", light: "rgba(16,185,129,0.12)", text: "#10B981" },
  "دليل تقني":      { bg: "#3B82F6", light: "rgba(59,130,246,0.12)",  text: "#3B82F6" },
  "إدارة المخزون":  { bg: "#F59E0B", light: "rgba(245,158,11,0.12)",  text: "#F59E0B" },
  "تحسين الأداء":   { bg: "#8B5CF6", light: "rgba(139,92,246,0.12)",  text: "#8B5CF6" },
  "إدارة الأعمال":  { bg: "#EF4444", light: "rgba(239,68,68,0.12)",   text: "#EF4444" },
};

const DEFAULT_COLOR = { bg: "#004EEB", light: "rgba(0,78,235,0.12)", text: "#004EEB" };

function getColor(cat) {
  return CATEGORY_COLORS[cat] || DEFAULT_COLOR;
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("ar-SA", { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return iso;
  }
}

function BlogCard({ post }) {
  const c = getColor(post.category);
  return (
    <Link to={`/blog/${post.slug}`}
      data-testid={`blog-card-${post.slug}`}
      className="group flex flex-col rounded-2xl bg-white border border-slate-200 overflow-hidden hover:border-slate-300 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">

      {/* Cover strip */}
      <div className="h-44 relative overflow-hidden shrink-0"
        style={{ background: `linear-gradient(135deg, ${c.bg}22 0%, ${c.bg}44 100%)` }}>
        <div className="absolute inset-0"
          style={{
            backgroundImage: `linear-gradient(${c.bg}22 1px, transparent 1px), linear-gradient(90deg, ${c.bg}22 1px, transparent 1px)`,
            backgroundSize: "28px 28px"
          }} />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center"
            style={{ background: `${c.bg}20`, border: `1.5px solid ${c.bg}30` }}>
            <BookOpen className="w-7 h-7" style={{ color: c.bg }} strokeWidth={1.5} />
          </div>
        </div>
        <div className="absolute top-4 right-4">
          <span className="px-2.5 py-1 rounded-full text-xs font-bold"
            style={{ background: `${c.bg}20`, color: c.bg, border: `1px solid ${c.bg}30` }}>
            {post.category}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-col flex-1 p-6">
        <h2 className="font-bold text-slate-900 text-base leading-snug mb-2 line-clamp-2 group-hover:text-[#004EEB] transition-colors">
          {post.title}
        </h2>
        <p className="text-slate-500 text-sm leading-relaxed line-clamp-2 flex-1 mb-4">
          {post.excerpt}
        </p>

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t border-slate-100">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0"
              style={{ background: post.author_color || "#004EEB" }}>
              {post.author_initials || "ج"}
            </div>
            <div>
              <div className="text-xs font-semibold text-slate-700 leading-none">{post.author}</div>
              <div className="text-xs text-slate-400 mt-0.5">{formatDate(post.published_at)}</div>
            </div>
          </div>
          <div className="flex items-center gap-1 text-slate-400 text-xs">
            <Clock className="w-3.5 h-3.5" />
            {post.read_time} دقائق
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function BlogPage() {
  const { name: platformName, logo_base64 } = usePlatform();
  const [posts, setPosts] = useState(null);
  const [activeCategory, setActiveCategory] = useState("الكل");

  useEffect(() => {
    document.title = `المدونة — ${platformName || "جسر"}`;
    window.scrollTo(0, 0);
    api.get("/public/blog")
      .then(r => setPosts(r.data?.posts || []))
      .catch(() => setPosts([]));
  }, [platformName]);

  const categories = ["الكل", ...Array.from(new Set((posts || []).map(p => p.category).filter(Boolean)))];
  const filtered = !posts ? [] : activeCategory === "الكل" ? posts : posts.filter(p => p.category === activeCategory);

  return (
    <div dir="rtl" className="min-h-screen bg-[#F9FAFB]" style={{ fontFamily: "'Tajawal', sans-serif" }}>

      {/* Navbar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            {logo_base64
              ? <img src={logo_base64} alt={platformName} className="w-7 h-7 rounded-lg object-contain" />
              : <JasrMark className="w-7 h-7" />}
            <span className="font-bold text-slate-900">{platformName || "جسر"}</span>
          </Link>
          <Link to="/" className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#004EEB] transition-colors">
            <ChevronLeft className="w-4 h-4" />
            الرئيسية
          </Link>
        </div>
      </header>

      {/* Hero */}
      <div className="bg-[#030B1A] py-16 sm:py-24 px-4 text-center relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: `linear-gradient(rgba(0,78,235,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(0,78,235,0.04) 1px, transparent 1px)`,
          backgroundSize: "40px 40px"
        }} />
        <div className="absolute top-0 left-1/3 w-96 h-96 pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(0,78,235,0.10) 0%, transparent 65%)" }} />
        <div className="relative">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-5 text-xs font-bold"
            style={{ background: "rgba(0,78,235,0.15)", border: "1px solid rgba(0,78,235,0.3)", color: "#93C5FD" }}>
            <BookOpen className="w-3.5 h-3.5" /> المدونة
          </div>
          <h1 className="text-3xl sm:text-5xl font-bold text-white mb-4">مدونة جسر</h1>
          <p className="text-slate-400 text-base sm:text-lg max-w-xl mx-auto">
            نصائح ودلائل عملية لأصحاب المتاجر — لتنمو أعمالك أسرع وتدير متجرك بذكاء.
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16">

        {/* Category filter */}
        {posts && posts.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap mb-10">
            {categories.map(cat => (
              <button key={cat} onClick={() => setActiveCategory(cat)}
                data-testid={`blog-cat-${cat}`}
                className="px-4 py-2 rounded-full text-sm font-semibold transition-all"
                style={activeCategory === cat
                  ? { background: "#004EEB", color: "#fff" }
                  : { background: "#fff", color: "#64748B", border: "1px solid #E2E8F0" }}>
                {cat}
              </button>
            ))}
          </div>
        )}

        {/* Loading */}
        {!posts && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="rounded-2xl bg-white border border-slate-200 overflow-hidden animate-pulse">
                <div className="h-44 bg-slate-100" />
                <div className="p-6 space-y-3">
                  <div className="h-4 bg-slate-100 rounded w-3/4" />
                  <div className="h-3 bg-slate-100 rounded w-full" />
                  <div className="h-3 bg-slate-100 rounded w-2/3" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Posts grid */}
        {posts && filtered.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map(post => <BlogCard key={post.id} post={post} />)}
          </div>
        )}

        {/* Empty */}
        {posts && filtered.length === 0 && (
          <div className="text-center py-20">
            <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <BookOpen className="w-8 h-8 text-slate-400" strokeWidth={1.5} />
            </div>
            <p className="text-slate-500 text-base">لا توجد مقالات في هذا التصنيف حالياً.</p>
          </div>
        )}
      </div>

      {/* Footer note */}
      <div className="border-t border-slate-200 py-8 text-center px-4">
        <div className="flex items-center justify-center gap-4 text-sm">
          <Link to="/" className="text-[#004EEB] hover:underline">الرئيسية</Link>
          <span className="text-slate-300">·</span>
          <Link to="/contact" className="text-[#004EEB] hover:underline">تواصل معنا</Link>
          <span className="text-slate-300">·</span>
          <Link to="/privacy" className="text-[#004EEB] hover:underline">الخصوصية</Link>
        </div>
      </div>
    </div>
  );
}
